var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { observe, observable, action } from "mobx";
import { SelectionType, ItemState } from "../data";
import { MasterEventParser } from "./MasterEventParser";
import { LoginControlHelper } from "./helpers/LoginControlHelper";
import { MarketParser } from "./MarketParser";
import Debounce from "debounce-decorator";
import { MarketGetter, SelectionParser } from "../parser";
import { globalTimeTicker } from "sb-resp-utils";
import { eBranches, OddStyles } from "sb-helpers";
import { enableLowOdds } from "../helpers";
export var eventTypeForGameBlock;
(function (eventTypeForGameBlock) {
    eventTypeForGameBlock[eventTypeForGameBlock["Prelive"] = 0] = "Prelive";
    eventTypeForGameBlock[eventTypeForGameBlock["Live"] = 1] = "Live";
    eventTypeForGameBlock[eventTypeForGameBlock["Outright"] = 99] = "Outright";
})(eventTypeForGameBlock || (eventTypeForGameBlock = {}));
export var MainMarketsMapping;
(function (MainMarketsMapping) {
    MainMarketsMapping[MainMarketsMapping["ML"] = 1] = "ML";
    MainMarketsMapping[MainMarketsMapping["HC"] = 2] = "HC";
    MainMarketsMapping[MainMarketsMapping["OU"] = 3] = "OU";
})(MainMarketsMapping || (MainMarketsMapping = {}));
/*
    The class has 'istanbul ignore' flag , because of testing typescript interface with Jest.
    It`s known issue : (https://github.com/kulshekhar/ts-jest/issues/378)
    The class is fully tested, but property "events" implement interface "EventsObject" which is not
    testable with Jest - there is no types like in typescript
 */
/* istanbul ignore next */
export class DataParser {
    constructor(dataFacade, decimalLocalizator) {
        this.payloadsCache = [];
        this.dataFacade = dataFacade;
        this.allItems = dataFacade.cms.SuperBannerItems;
        this.navHandler = dataFacade.navHandler;
        this.items = [];
        this.currentSelectedSportId = dataFacade.currentSelectedSportId;
        this.decimalLocalizator = decimalLocalizator;
        this.marketGetter = new MarketGetter();
        this.isInitialLoad = true;
        this.updateItemsForDevice();
        this.updateItemState(false);
        this.disposeDataCollection = observe(this.dataFacade, "dataCollection", (change) => {
            this.updateCollection(change.newValue);
        });
        this.disposeShouldRemoveEvents = observe(this.dataFacade, "shouldRemoveEvents", (change) => {
            if (change.newValue === true) {
                this.removeAllEvents();
                this.updateItemsState();
            }
        });
        this.disposeTimeZone = observe(this.dataFacade, "currentTimeZone", (change) => {
            this.updateEventsDate(change.newValue);
        });
        this.disposeOddStyleChange = observe(this.dataFacade, "currentOddStyle", (change) => {
            this.updateEventsOddStyle(change.newValue);
        });
        this.disposeOddStyleChange = observe(this.dataFacade, "isUserLoggedIn", (change) => {
            this.updateItemState(change.newValue);
        });
        this.disposeCurrentSelectedSportIdChange = observe(this.dataFacade, "currentSelectedSportId", (change) => {
            this.updateCurrentSelectedSportId(change.newValue);
        });
        this.eventTimeTickingDisposer = observe(globalTimeTicker, (change) => {
            let changedObject = change.object;
            this.updateTimeElapsed(changedObject.interval);
        });
        this.disposeBetslipItemsChange = observe(this.dataFacade, "betslipItems", (change) => {
            this.updateSelectionSelectedState(change.newValue);
        });
    }
    activate() {
        this.dataFacade.activate();
    }
    deactive() {
        this.disposeTimeZone();
        this.disposeOddStyleChange();
        this.disposeDataCollection();
        this.disposeCurrentSelectedSportIdChange();
        this.disposeBetslipItemsChange();
    }
    /* istanbul ignore next */
    updateCollection(dataCollection) {
        if (dataCollection !== undefined) {
            this.payloadsCache.push(dataCollection);
            this.applyChanges();
        }
    }
    /* istanbul ignore next */
    applyChanges() {
        this.applyCachedChanges(this.payloadsCache);
        this.payloadsCache = [];
    }
    applyCachedChanges(changes) {
        changes.forEach(dataCollection => {
            this.removeEvents(dataCollection.data.remove.events);
            this.removeMarkets(dataCollection.data.remove.markets);
            this.upsertEvents(dataCollection.data.add.events);
            this.upsertMarkets(dataCollection.data.add.markets);
            this.upsertEvents(dataCollection.data.change.events);
            this.upsertMarkets(dataCollection.data.change.markets);
        });
        this.formatSelections();
        this.updateItemsState();
        // Once loaded initial data - request betslip status
        this.requestInitialBetSlipStatus();
    }
    formatSelections() {
        this.iterateEvents(p => {
            if (p.market) {
                p.market.selections.forEach(p => {
                    p.updateFormattedPoint(this.formatPoints(p));
                    p.updateFormattedOdds(this.formatOdds(p.odds));
                });
            }
        });
    }
    formatPoints(selection) {
        const hasEnableLowOdds = enableLowOdds(selection.trueOdds, this.dataFacade.cms.EnableLowOdds);
        if (hasEnableLowOdds) {
            return "";
        }
        return (selection.points > 0 ? "+" : "") + this.decimalLocalizator.localizePoint(selection.points);
    }
    formatOdds(odds) {
        return this.decimalLocalizator.localizeOdd(odds, OddStyles[this.dataFacade.currentOddStyle]);
    }
    removeAllEvents() {
        this.iterateEvents(pickedEvent => {
            pickedEvent.deleteEvent();
            pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
        });
    }
    removeEvents(entries) {
        this.iterate(entries, id => {
            this.findEvent(id, pickedEvent => {
                pickedEvent.deleteEvent();
                pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
            });
        });
    }
    removeMarkets(marketIds) {
        if (marketIds.length > 0) {
            this.iterateEvents(pickedEvent => {
                pickedEvent.removeMarkets(marketIds);
                pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
            });
        }
    }
    upsertEvents(sportEvents) {
        this.iterate(sportEvents, sportEvent => {
            this.findEvent(sportEvent.id, pickedEvent => {
                try {
                    let parsedMasterEvent = MasterEventParser.instance.parse(sportEvent, pickedEvent.event, {
                        oddStyle: this.dataFacade.currentOddStyle,
                        timeZone: this.dataFacade.currentTimeZone,
                        isSwapped: sportEvent.isTeamSwap,
                        dateFormat: this.dataFacade.dateFormat,
                        timeFormat: this.dataFacade.timeFormat,
                        enableLowOdds: this.dataFacade.cms.EnableLowOdds
                    });
                    pickedEvent.upsertEvent(parsedMasterEvent);
                    if (pickedEvent.event) {
                        this.updateFormattedDate(pickedEvent.event);
                        pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
                    }
                }
                catch (ex) {
                    console.error(ex);
                }
            });
        });
    }
    upsertMarkets(markets) {
        this.iterate(markets, market => {
            this.findEvent(market.eventId, pickedEvent => {
                try {
                    if (pickedEvent.event) {
                        let existingMarket = this.existingMarket(pickedEvent.event, market);
                        let parsedMarket = MarketParser.instance.parse(market, existingMarket, {
                            oddStyle: this.dataFacade.currentOddStyle,
                            timeZone: this.dataFacade.currentTimeZone,
                            isSwapped: pickedEvent.event.isTeamSwapEnabled,
                            dateFormat: this.dataFacade.dateFormat,
                            timeFormat: this.dataFacade.timeFormat,
                            enableLowOdds: this.dataFacade.cms.EnableLowOdds
                        });
                        pickedEvent.upsertMarket(parsedMarket);
                        this.updateFormattedDate(pickedEvent.event, this.dataFacade.currentTimeZone);
                        pickedEvent.updateMarket(this.marketGetter.getCurrentMarket(pickedEvent));
                    }
                }
                catch (ex) {
                    console.error(ex);
                }
            });
        });
    }
    existingMarket(masterEvent, market) {
        return masterEvent.markets.find(m => m.id === market.id);
    }
    updateItemsState() {
        this.iterateEvents(pickedEvent => {
            if ((pickedEvent.MasterEventID || pickedEvent.EventID) && (!pickedEvent.event || !pickedEvent.market)) {
                pickedEvent.setState(ItemState.unavailable);
                return;
            }
            pickedEvent.setState(ItemState.available);
        });
    }
    updateEventsDate(newTimeZone) {
        this.iterateEvents(pickedEvent => {
            if (!pickedEvent.event) {
                return;
            }
            let masterEvent = pickedEvent.event;
            this.updateFormattedDate(masterEvent, newTimeZone);
        });
    }
    updateEventsOddStyle(newOddStyle) {
        this.iterateEvents(pickedEvent => {
            if (!pickedEvent.event) {
                return;
            }
            let masterEvent = pickedEvent.event;
            this.iterate(masterEvent.markets, market => {
                this.iterate(market.selections, selection => {
                    let rawOdds = SelectionParser.instance.getOddsForOddStyle(selection, newOddStyle);
                    selection.updateOdds(rawOdds);
                    selection.updateFormattedOdds(this.formatOdds(rawOdds));
                });
            });
        });
    }
    updateFormattedDate(masterEvent, newTimeZone) {
        let timezone = typeof newTimeZone !== "undefined" ? newTimeZone : this.dataFacade.currentTimeZone;
        let dateTime = MasterEventParser.instance.getFormattedDateTime(masterEvent.eventDateTime, timezone, this.dataFacade.dateFormat, this.dataFacade.timeFormat);
        masterEvent.updateDateTime(dateTime.Date, dateTime.Time);
    }
    updateTimeElapsed(interval) {
        this.iterateEvents(pickedEvent => {
            if (pickedEvent.event && pickedEvent.event.liveGameState) {
                var gameTime = MasterEventParser.instance.getLiveGameStateTime(pickedEvent.event.liveGameState, interval);
                pickedEvent.event.liveGameState.updateGameTime(gameTime);
                var countdownTime = MasterEventParser.instance.getLiveStartCountdownTime(pickedEvent.event.liveStartCountdown, interval);
                pickedEvent.event.updateLiveStartCountdown(countdownTime);
            }
        });
    }
    updateItemsForDevice() {
        this.items = this.dataFacade.deviceDetectHelper.itemDeviceFilter(this.allItems);
    }
    /* istanbul ignore next */
    updateItemState(isUserLoggedIn) {
        this.iterate(this.items, item => item.setState(LoginControlHelper.instance.validate(item, isUserLoggedIn)
            ? ItemState.available : ItemState.unavailable));
    }
    iterate(entries, action) {
        for (let index = 0; index < entries.length; index++) {
            action.call(this, entries[index]);
        }
    }
    iterateEvents(action) {
        this.iterate(this.items, item => {
            if (item.PickedEvent) {
                action.call(this, item.PickedEvent);
            }
        });
    }
    /* istanbul ignore next */
    findEvent(eventId, action) {
        this.iterateEvents(pickedEvent => {
            if (pickedEvent.MasterEventID.toString() === eventId || pickedEvent.EventID.toString() === eventId) {
                action(pickedEvent);
            }
        });
    }
    updateSelectionSelectedState(betslipItems) {
        // Remove status listener, until the status is updating
        this.dataFacade.removeBetslipStatusListener();
        const betslipSelectionIds = betslipItems.map(i => i.selectionId);
        this.iterateEvents((item) => {
            if (item.market) {
                item.market.selections.forEach(s => {
                    let isSelected = betslipSelectionIds.indexOf(s.id) > -1;
                    s.isSelected !== isSelected && s.updateIsSelected(isSelected);
                });
            }
        });
        this.dataFacade.addBetslipStatusListener();
    }
    imageClickHandler(e, pickedEvent, imageLink) {
        if (pickedEvent && pickedEvent.event) {
            this.openGameTranspiler(pickedEvent, e);
        }
        else if (imageLink) {
            e.preventDefault();
            window.location.href = imageLink;
        }
    }
    /* istanbul ignore next */
    addToSlipTranspiler(selection, items) {
        // Remove status listener, until the selection is added/removed from betslip
        this.dataFacade.removeBetslipStatusListener();
        const message = {
            receiverIds: [],
            items
        };
        selection.isSelected
            ? this.dataFacade.sendRemoveSelectionToBetslipMsg(message)
            : this.dataFacade.sendAddSelectionToBetslipMsg(message);
        selection.updateIsSelected(!selection.isSelected);
    }
    /* istanbul ignore next */
    openGameTranspiler(pickedEvent, e) {
        if (!pickedEvent || !pickedEvent.event) {
            return;
        }
        let event = pickedEvent.event;
        e.preventDefault();
        let mouseEvent = Object.assign(Object.assign({}, e), { target: e.currentTarget });
        let openGameData = {
            event: mouseEvent,
            id: parseInt(event.id),
            isLive: event.isLive,
            evTypeForGameBlock: this.getEventTypeForGameBlock(pickedEvent),
            shouldHideSidePanels: false,
            branchID: parseInt(event.sportID),
            regionID: parseInt(event.regionId)
        };
        if (openGameData.branchID === eBranches.HorseRacing
            || openGameData.branchID === eBranches.GreyHoundsRacing) {
            OpenRacingGame(openGameData.event, openGameData.id, openGameData.regionID, pickedEvent.Mode ? parseInt(pickedEvent.Mode) : 0, openGameData.branchID, 0, 0, 0, 0, 0, 0, 0, 0);
        }
        else {
            this.navHandler.OpenGame(openGameData.event, openGameData.id, openGameData.isLive, openGameData.evTypeForGameBlock, openGameData.shouldHideSidePanels, openGameData.branchID);
        }
    }
    /* istanbul ignore next */
    openRaceGame(link) {
        return window.location.href = link;
    }
    getEventTypeForGameBlock(pickedEvent) {
        let event = pickedEvent.event;
        if (pickedEvent.IsOutright) {
            return eventTypeForGameBlock.Outright;
        }
        if (event.isLive) {
            return eventTypeForGameBlock.Live;
        }
        else {
            return eventTypeForGameBlock.Prelive;
        }
    }
    /* istanbul ignore next */
    getMarketId(market) {
        if (this.isMainMarket(market)) {
            return market.id.substring(2, market.id.length);
        }
        return market.id;
    }
    /* istanbul ignore next */
    getMarketTypeId(market) {
        if (market.type === SelectionType.QA) {
            return market.typeId;
        }
        if (this.isMainMarket(market)) {
            return market.typeId.substring(2, market.typeId.length);
        }
        return market.typeId;
    }
    updateCurrentSelectedSportId(sportId) {
        this.currentSelectedSportId = sportId;
    }
    isMainMarket(market) {
        return market && (market.id.charAt(0) === MainMarketsMapping.ML.toString())
            || (market.id.charAt(0) === MainMarketsMapping.HC.toString())
            || (market.id.charAt(0) === MainMarketsMapping.OU.toString());
    }
    requestInitialBetSlipStatus() {
        if (this.isInitialLoad) {
            this.dataFacade.requestBetslipStatus();
        }
        else {
            this.isInitialLoad = false;
        }
    }
}
__decorate([
    observable,
    __metadata("design:type", Number)
], DataParser.prototype, "currentSelectedSportId", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], DataParser.prototype, "items", void 0);
__decorate([
    Debounce(500),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "applyChanges", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "applyCachedChanges", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "updateItemsForDevice", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "updateItemState", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "updateSelectionSelectedState", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], DataParser.prototype, "updateCurrentSelectedSportId", null);
//# sourceMappingURL=DataParser.js.map